import {
  BarPieChartData,
  ChartData,
  DataPoint,
  FMILanguage,
  AppVersion,
  User,
  ManualOpenDataPoint,
} from "./../index";

import { parseUtcAsLocal } from "app/shared/utils/date-utils";

enum UserType {
  ApplicationAdministrator = 1,
  KomatsuEmployee = 2,
  ModifiedDistributorCustomer = 5,
  ParentCompany = 3,
  Minesite = 4,
}

export function getUserTypeLabel(userType: UserType): string {
  return UserType[userType].replace(/([a-z])([A-Z])/g, "$1 $2");
}

export class UserBarPieChartData<T> extends BarPieChartData {
  DataPoints: T[];
  DataPointsForEmployees: T[];
  DataPointsForCustomers: T[];
  DataPointsForApplicationAdministrator: T[];
  DataPointsForKomatsuEmployee: T[];
  DataPointsForParentCompany: T[];
  DataPointsForMinesite: T[];
  DataPointsForModifiedDistributorCustomer: T[];
  TotalForEmployees: number;
  UniqueForEmployees: number;
  TotalForCustomers: number;
  UniqueForCustomers: number;
  TotalForApplicationAdministrator: number;
  UniqueForApplicationAdministrator: number;
  TotalForKomatsuEmployee: number;
  UniqueForKomatsuEmployee: number;
  TotalForParentCompany: number;
  UniqueForParentCompany: number;
  TotalForMinesite: number;
  UniqueForMinesite: number;
  TotalForModifiedDistributorCustomer: number;
  UniqueForModifiedDistributorCustomer: number;

  constructor(data?: Partial<UserBarPieChartData<T>>) {
    super();
    Object.assign(this, data);
  }
}

export class UserLineChartData<T> extends ChartData {
  DataPoints: T[];
  DataPointsForEmployees: T[];
  DataPointsForCustomers: T[];
  DataPointsForApplicationAdministrator: T[];
  DataPointsForKomatsuEmployee: T[];
  DataPointsForParentCompany: T[];
  DataPointsForMinesite: T[];
  DataPointsForModifiedDistributorCustomer: T[];
  MaxForEmployees: number;
  MinForEmployees: number;
  MaxForCustomers: number;
  MinForCustomers: number;
  MaxForApplicationAdministrator: number;
  MinForApplicationAdministrator: number;
  MaxForKomatsuEmployee: number;
  MinForKomatsuEmployee: number;
  MaxForParentCompany: number;
  MinForParentCompany: number;
  MaxForMinesite: number;
  MinForMinesite: number;
  MaxForModifiedDistributorCustomer: number;
  MinForModifiedDistributorCustomer: number;

  constructor(data?: Partial<UserLineChartData<T>>) {
    super();
    Object.assign(this, data);
  }
}

export class UserSessionsChartData {
  DataPoints: UserSessionsChartDataPoint[];
  DataPointsForEmployees: UserSessionsChartDataPoint[];
  DataPointsForCustomers: UserSessionsChartDataPoint[];
  DataPointsForApplicationAdministrator: UserSessionsChartDataPoint[];
  DataPointsForKomatsuEmployee: UserSessionsChartDataPoint[];
  DataPointsForParentCompany: UserSessionsChartDataPoint[];
  DataPointsForMinesite: UserSessionsChartDataPoint[];
  DataPointsForModifiedDistributorCustomer: UserSessionsChartDataPoint[];
  TotalSessions: number;
  ActiveSessions: number;
  TotalUsers: number;
  TotalSessionsForEmployees: number;
  ActiveSessionsForEmployees: number;
  TotalEmployeeUsers: number;
  TotalSessionsForCustomers: number;
  ActiveSessionsForCustomers: number;
  TotalCustomerUsers: number;
  TotalSessionsForApplicationAdministrator: number;
  ActiveSessionsForApplicationAdministrator: number;
  TotalApplicationAdministratorUsers: number;
  TotalSessionsForKomatsuEmployees: number;
  ActiveSessionsForKomatsuEmployees: number;
  TotalKomatsuEmployeeUsers: number;
  TotalSessionsForParentCompany: number;
  ActiveSessionsForParentCompany: number;
  TotalParentCompanyUsers: number;
  TotalSessionsForMinesite: number;
  ActiveSessionsForMinesite: number;
  TotalMinesiteUsers: number;
  TotalSessionsForModifiedDistributorCustomer: number;
  ActiveSessionsForModifiedDistributorCustomer: number;
  TotalModifiedDistributorCustomerUsers: number;
  StartDate: Date;
  EndDate: Date;
  constructor(data?: Partial<UserSessionsChartData>) {
    Object.assign(this, data);

    // Create instances for DataPoints arrays
    this.DataPoints =
      data?.DataPoints?.map((dp) => new UserSessionsChartDataPoint(dp)) || [];
    this.DataPointsForEmployees =
      data?.DataPointsForEmployees?.map(
        (dp) => new UserSessionsChartDataPoint(dp)
      ) || [];
    this.DataPointsForCustomers =
      data?.DataPointsForCustomers?.map(
        (dp) => new UserSessionsChartDataPoint(dp)
      ) || [];
    this.DataPointsForApplicationAdministrator =
      data?.DataPointsForApplicationAdministrator?.map(
        (dp) => new UserSessionsChartDataPoint(dp)
      ) || [];
    this.DataPointsForKomatsuEmployee =
      data?.DataPointsForKomatsuEmployee?.map(
        (dp) => new UserSessionsChartDataPoint(dp)
      ) || [];
    this.DataPointsForParentCompany =
      data?.DataPointsForParentCompany?.map(
        (dp) => new UserSessionsChartDataPoint(dp)
      ) || [];
    this.DataPointsForMinesite =
      data?.DataPointsForMinesite?.map(
        (dp) => new UserSessionsChartDataPoint(dp)
      ) || [];
    this.DataPointsForModifiedDistributorCustomer =
      data?.DataPointsForModifiedDistributorCustomer?.map(
        (dp) => new UserSessionsChartDataPoint(dp)
      ) || [];

    if (data?.StartDate) this.StartDate = parseUtcAsLocal(data.StartDate);
    if (data?.EndDate) this.EndDate = parseUtcAsLocal(data.EndDate);
  }
}
export class AppUsageChartData {
  DataPoints: AppUsageChartDataPoint[];
  DataPointsForEmployees: AppUsageChartDataPoint[];
  DataPointsForCustomers: AppUsageChartDataPoint[];
  DataPointsForApplicationAdministrator: AppUsageChartDataPoint[];
  DataPointsForKomatsuEmployee: AppUsageChartDataPoint[];
  DataPointsForParentCompany: AppUsageChartDataPoint[];
  DataPointsForMinesite: AppUsageChartDataPoint[];
  DataPointsForModifiedDistributorCustomer: AppUsageChartDataPoint[];
  TotalSessionsForAll: number;
  AvgSessionTimeForAll: number;
  TotalSessionTimeForAll: number;
  UsageDataForAll: ManualOpenDataPoint[];
  TotalSessionsForEmployees: number;
  AvgSessionTimeForEmployees: number;
  TotalSessionTimeForEmployees: number;
  UsageDataForAllEmployees: ManualOpenDataPoint[];
  TotalSessionsForCustomers: number;
  AvgSessionTimeForCustomers: number;
  TotalSessionTimeForCustomers: number;
  UsageDataForAllCustomers: ManualOpenDataPoint[];
  TotalSessionsForApplicationAdministrator: number;
  AvgSessionTimeForApplicationAdministrator: number;
  TotalSessionTimeForApplicationAdministrator: number;
  UsageDataForAllApplicationAdministrator: ManualOpenDataPoint[];
  TotalSessionsForKomatsuEmployees: number;
  AvgSessionTimeForKomatsuEmployees: number;
  TotalSessionTimeForKomatsuEmployees: number;
  UsageDataForAllKomatsuEmployees: ManualOpenDataPoint[];
  TotalSessionsForParentCompany: number;
  AvgSessionTimeForParentCompany: number;
  TotalSessionTimeForParentCompany: number;
  UsageDataForAllParentCompany: ManualOpenDataPoint[];
  TotalSessionsForMinesite: number;
  AvgSessionTimeForMinesite: number;
  TotalSessionTimeForMinesite: number;
  UsageDataForAllMinesite: ManualOpenDataPoint[];
  TotalSessionsForModifiedDistributorCustomer: number;
  AvgSessionTimeForModifiedDistributorCustomer: number;
  TotalSessionTimeForModifiedDistributorCustomer: number;
  UsageDataForAllModifiedDistributorCustomer: ManualOpenDataPoint[];
  StartDate: Date;
  EndDate: Date;
  constructor(data?: Partial<AppUsageChartData>) {
    Object.assign(this, data);

    // Create instances for DataPoints arrays
    this.DataPoints =
      data?.DataPoints?.map((dp) => new AppUsageChartDataPoint(dp)) || [];
    this.DataPointsForEmployees =
      data?.DataPointsForEmployees?.map(
        (dp) => new AppUsageChartDataPoint(dp)
      ) || [];
    this.DataPointsForCustomers =
      data?.DataPointsForCustomers?.map(
        (dp) => new AppUsageChartDataPoint(dp)
      ) || [];
    this.DataPointsForApplicationAdministrator =
      data?.DataPointsForApplicationAdministrator?.map(
        (dp) => new AppUsageChartDataPoint(dp)
      ) || [];
    this.DataPointsForKomatsuEmployee =
      data?.DataPointsForKomatsuEmployee?.map(
        (dp) => new AppUsageChartDataPoint(dp)
      ) || [];
    this.DataPointsForParentCompany =
      data?.DataPointsForParentCompany?.map(
        (dp) => new AppUsageChartDataPoint(dp)
      ) || [];
    this.DataPointsForMinesite =
      data?.DataPointsForMinesite?.map(
        (dp) => new AppUsageChartDataPoint(dp)
      ) || [];
    this.DataPointsForModifiedDistributorCustomer =
      data?.DataPointsForModifiedDistributorCustomer?.map(
        (dp) => new AppUsageChartDataPoint(dp)
      ) || [];

    // Create instances for UsageData arrays
    this.UsageDataForAll =
      data?.UsageDataForAll?.map((ud) => new ManualOpenDataPoint(ud)) || [];
    this.UsageDataForAllEmployees =
      data?.UsageDataForAllEmployees?.map(
        (ud) => new ManualOpenDataPoint(ud)
      ) || [];
    this.UsageDataForAllCustomers =
      data?.UsageDataForAllCustomers?.map(
        (ud) => new ManualOpenDataPoint(ud)
      ) || [];
    this.UsageDataForAllApplicationAdministrator =
      data?.UsageDataForAllApplicationAdministrator?.map(
        (ud) => new ManualOpenDataPoint(ud)
      ) || [];
    this.UsageDataForAllKomatsuEmployees =
      data?.UsageDataForAllKomatsuEmployees?.map(
        (ud) => new ManualOpenDataPoint(ud)
      ) || [];
    this.UsageDataForAllParentCompany =
      data?.UsageDataForAllParentCompany?.map(
        (ud) => new ManualOpenDataPoint(ud)
      ) || [];
    this.UsageDataForAllMinesite =
      data?.UsageDataForAllMinesite?.map((ud) => new ManualOpenDataPoint(ud)) ||
      [];
    this.UsageDataForAllModifiedDistributorCustomer =
      data?.UsageDataForAllModifiedDistributorCustomer?.map(
        (ud) => new ManualOpenDataPoint(ud)
      ) || [];

    if (data?.StartDate) this.StartDate = parseUtcAsLocal(data.StartDate);
    if (data?.EndDate) this.EndDate = parseUtcAsLocal(data.EndDate);
  }
}

export class DataPointForUserAnalytics extends DataPoint {
  TypeOfUser: UserType;
  PermissionType: string;
  PermissionId: number;

  constructor(data?: Partial<DataPointForUserAnalytics>) {
    super();
    Object.assign(this, data);
  }
}
export class UserLineChartDataPoint extends DataPointForUserAnalytics {
  Index: number;
  RangeStart: Date;
  RangeEnd: Date;
  PermissionGroups: UserLineChartDataPoint[];

  constructor(data?: Partial<UserLineChartDataPoint>) {
    super();
    Object.assign(this, data);
    if (data?.RangeStart) this.RangeStart = parseUtcAsLocal(data.RangeStart);
    if (data?.RangeEnd) this.RangeEnd = parseUtcAsLocal(data.RangeEnd);
    this.PermissionGroups =
      data?.PermissionGroups?.map((pg) => new UserLineChartDataPoint(pg)) || [];
  }
}
//TODO: remove, replace use with DataPointForUserAnalytics
export class UserAnalyticsDataPoint extends DataPoint {
  TypeOfUser: UserType;
  PermissionType: string;
  PermissionId: number;

  constructor(data?: Partial<UserAnalyticsDataPoint>) {
    super();
    Object.assign(this, data);
  }
}

export class UserSessionsChartDataPoint {
  TypeOfUser: UserType;
  PermissionType: string;
  PermissionId: number;
  TotalSessions: number;
  ActiveSessions: number;
  TotalUsers: number;

  constructor(data?: Partial<UserSessionsChartDataPoint>) {
    Object.assign(this, data);
  }
}

export class AppUsageChartDataPoint {
  TypeOfUser: UserType;
  TotalSessions: number;
  AvgSessionTime: number; // in milliseconds
  TotalSessionTime: number; // in milliseconds
  UsageData: AppUsageUser[];

  constructor(data?: Partial<AppUsageChartDataPoint>) {
    Object.assign(this, data);
    this.UsageData = data?.UsageData?.map((u) => new AppUsageUser(u)) || [];
  }

  get PermissionType(): string {
    return getUserTypeLabel(this.TypeOfUser);
  }
}

export class AppUsageSession {
  SessionId: string;
  TotalSessionTime: number;
  LoggedIn: Date;
  ManualsOpened: ManualOpenDataPoint[];

  constructor(data?: Partial<AppUsageSession>) {
    Object.assign(this, data);
    this.ManualsOpened =
      data?.ManualsOpened?.map((m) => new ManualOpenDataPoint(m)) || [];
    if (data?.LoggedIn) this.LoggedIn = parseUtcAsLocal(data.LoggedIn);
  }
}

export class AppUsageUser {
  TypeOfUser: UserType;
  PermissionId: number;
  UserId: number;
  FirstName: string;
  LastName: string;
  UserName: string;
  TotalSessionTime: number;
  AppUsageSessions: AppUsageSession[];

  constructor(data?: Partial<AppUsageUser>) {
    Object.assign(this, data);
    this.AppUsageSessions =
      data?.AppUsageSessions?.map((s) => new AppUsageSession(s)) || [];
  }
}

export class FilteredUserActionDataPoint {
  TypeOfUser: UserType;
  ModifiedBy: string;
  Date: Date;
  User: User;

  constructor(data?: Partial<FilteredUserActionDataPoint>) {
    Object.assign(this, data);
    if (data?.Date) this.Date = parseUtcAsLocal(data.Date);
  }
}

export class ApplicationLanguagesData extends UserBarPieChartData<ApplicationLangData> {
  constructor(data?: Partial<ApplicationLanguagesData>) {
    super();
    Object.assign(this, data);
    if (data?.DataPoints) {
      this.DataPoints = data.DataPoints.map(
        (dp) => new ApplicationLangData(dp)
      );
    }
  }
}

export class ApplicationLangData extends DataPointForUserAnalytics {
  Language: FMILanguage;

  constructor(data?: Partial<ApplicationLangData>) {
    super();
    Object.assign(this, data);
  }
}

export class ApplicationVersionsData extends UserBarPieChartData<ApplicationVersionData> {
  constructor(data?: Partial<ApplicationVersionsData>) {
    super();
    Object.assign(this, data);
    if (data?.DataPoints) {
      this.DataPoints = data.DataPoints.map(
        (dp) => new ApplicationVersionData(dp)
      );
    }
  }
}

export class ApplicationVersionData extends DataPointForUserAnalytics {
  Version: AppVersion;

  constructor(data?: Partial<ApplicationVersionData>) {
    super();
    Object.assign(this, data);
    if (data?.Version) {
      this.Version = new AppVersion(data.Version);
    }
  }
}

export class OsVersionsData extends UserBarPieChartData<OsVersionData> {
  constructor(data?: Partial<OsVersionsData>) {
    super();
    Object.assign(this, data);
    if (data?.DataPoints) {
      this.DataPoints = data.DataPoints.map((dp) => new OsVersionData(dp));
    }
  }
}

export class OsVersionData extends DataPointForUserAnalytics {
  AppType: string;
  OsVersion: string;

  constructor(data?: Partial<OsVersionData>) {
    super();
    Object.assign(this, data);
  }
}

export class UsersCreatedDataTotal extends UserBarPieChartData<UserCreatedData> {
  constructor(data?: Partial<UsersCreatedDataTotal>) {
    super();
    Object.assign(this, data);
    if (data?.DataPoints) {
      this.DataPoints = data.DataPoints.map((dp) => new UserCreatedData(dp));
    }
  }
}

export class UsersCreatedData extends UserBarPieChartData<DataPointForUserAnalytics> {
  User: User;

  constructor(data?: Partial<UsersCreatedData>) {
    super();
    Object.assign(this, data);
    if (data?.DataPoints) {
      this.DataPoints = data.DataPoints.map((dp) => new UserCreatedData(dp));
    }
  }
}

export class UserCreatedData extends DataPointForUserAnalytics {
  User: User;
  CreatedBy: string;

  constructor(data?: Partial<UserCreatedData>) {
    super();
    Object.assign(this, data);

    if (data?.User) {
      this.User = new User(data.User);
    }
  }
}

export class UsersRemovedDataTotal extends UserBarPieChartData<UserRemovedDataTotal> {
  RemoveActions: FilteredUserActionDataPoint[];

  constructor(data?: Partial<UsersRemovedDataTotal>) {
    super();
    Object.assign(this, data);
    if (data?.DataPoints) {
      this.DataPoints = data.DataPoints.map(
        (dp) => new UserRemovedDataTotal(dp)
      );
    }
    this.RemoveActions =
      data?.RemoveActions?.map(
        (action) => new FilteredUserActionDataPoint(action)
      ) || [];
  }
}

export class UserRemovedDataTotal extends DataPointForUserAnalytics {
  constructor(data?: Partial<UserRemovedDataTotal>) {
    super();
    Object.assign(this, data);
  }
}

export class UsersLockedDataTotal extends UserBarPieChartData<UserLockedDataTotal> {
  LockActions: FilteredUserActionDataPoint[];

  constructor(data?: Partial<UsersLockedDataTotal>) {
    super();
    Object.assign(this, data);
    if (data?.DataPoints) {
      this.DataPoints = data.DataPoints.map(
        (dp) => new UserLockedDataTotal(dp)
      );
    }
    this.LockActions =
      data?.LockActions?.map(
        (action) => new FilteredUserActionDataPoint(action)
      ) || [];
  }
}

export class UsersRemovedData extends UserBarPieChartData<UserRemovedData> {
  constructor(data?: Partial<UsersRemovedData>) {
    super();
    Object.assign(this, data);
    if (data?.DataPoints) {
      this.DataPoints = data.DataPoints.map((dp) => new UserRemovedData(dp));
    }
  }
}

export class UserRemovedData extends DataPointForUserAnalytics {
  User: User;
  RemovedBy: string;
  RemovedDate: Date;

  get RemoveDate(): string {
    return this.RemovedDate
      ? this.RemovedDate.toLocaleDateString() +
          " " +
          this.RemovedDate.toLocaleTimeString()
      : "";
  }

  constructor(data?: Partial<UserRemovedData>) {
    super();
    Object.assign(this, data);
    if (data?.RemovedDate) this.RemovedDate = parseUtcAsLocal(data.RemovedDate);
  }
}

export class UsersLockedData extends UserBarPieChartData<UserLockedData> {
  constructor(data?: Partial<UsersLockedData>) {
    super();
    Object.assign(this, data);
    if (data?.DataPoints) {
      this.DataPoints = data.DataPoints.map((dp) => new UserLockedData(dp));
    }
  }
}

export class UserLockedData extends DataPointForUserAnalytics {
  User: User;
  LockedBy: string;
  LockedDate: Date;

  get LockDate(): string {
    return this.LockedDate
      ? this.LockedDate.toLocaleDateString() +
          " " +
          this.LockedDate.toLocaleTimeString()
      : "";
  }

  constructor(data?: Partial<UserLockedData>) {
    super();
    Object.assign(this, data);
    if (data?.LockedDate) this.LockedDate = parseUtcAsLocal(data.LockedDate);
  }
}
export class UserLockedDataTotal extends DataPointForUserAnalytics {
  constructor(data?: Partial<UserLockedDataTotal>) {
    super();
    Object.assign(this, data);
  }
}

export class UsersCurrentData extends UserBarPieChartData<UserAnalyticsDataPoint> {
  constructor(data?: Partial<UsersCurrentData>) {
    super();
    Object.assign(this, data);
    if (data?.DataPoints) {
      this.DataPoints = data.DataPoints.map(
        (dp) => new UserAnalyticsDataPoint(dp)
      );
    }
  }
}

export class UsersTotalData extends UserLineChartData<UserLineChartDataPoint> {
  constructor(data?: Partial<UsersTotalData>) {
    super();
    Object.assign(this, data);
    if (data?.DataPoints) {
      this.DataPoints = data.DataPoints.map(
        (dp) => new UserLineChartDataPoint(dp)
      );
    }
  }
}

export class UserSessionsData extends UserLineChartData<UserLineChartDataPoint> {
  constructor(data?: Partial<UserSessionsData>) {
    super();
    Object.assign(this, data);
    if (data?.DataPoints) {
      this.DataPoints = data.DataPoints.map(
        (dp) => new UserLineChartDataPoint(dp)
      );
    }
  }
}
